import CTA from 'components/Campaign/CTA';
import Anchor from 'components/typography/anchor';
import { RECURRING_PERIODS } from 'lib/stripe';
import { useEffect, useState } from 'react';
import { BarLoader } from 'react-spinners';
import config from 'siteconfig';
import useSWR from 'swr';
import { hasActiveSubscription } from 'utilities/stripe/helpers';

import { ExistingPaymentMethodDisplay } from './PaymentMethodDisplay';

const API_STRIPE_CUSTOMER = '/api/stripe/customer';
const MILLISECONDS_TO_WAIT = 15 * 1000;

const AnonymousUser = ({ keyword = 'premium' }) => {
  return (
    <div className="card-flat flex-col gap-4 text-center">
      <p className="mx-auto max-w-prose text-lg">
        So we know what account to upgrade or deposit coins to, please connect
        to your Resistbot&nbsp;account.
      </p>
      <CTA copy="Connect to Resistbot" keyword={keyword} />
      <p className="text-sm">
        Don’t care about perks?{' '}
        <Anchor className="anchor-text" href={config.donateUrls.stripe}>
          Donate here
        </Anchor>
        &nbsp;instead.
      </p>
    </div>
  );
};

const CurrentSubscriber = ({
  customer,
  paymentMethod,
  cancelAt,
  currentPeriodEnd,
}) => {
  const scheduledToCancel = cancelAt && cancelAt > 0;
  let planDescription;

  if (scheduledToCancel) {
    planDescription = (
      <p className="text-lg">
        Welcome back! Your premium membership will end on{' '}
        {new Date(currentPeriodEnd * 1000).toLocaleDateString()}.{' '}
        <Anchor className="anchor-text" href={customer.billingPortalSession}>
          Renew membership
        </Anchor>
      </p>
    );
  } else {
    planDescription = (
      <p className="text-lg">
        Welcome back! You have an active{' '}
        <strong>
          {customer.subscription.plan.interval === RECURRING_PERIODS.MONTH
            ? 'monthly'
            : 'yearly'}
        </strong>{' '}
        membership that will renew on{' '}
        {new Date(currentPeriodEnd * 1000).toLocaleDateString()}.{' '}
        <Anchor className="anchor-text" href={customer.billingPortalSession}>
          Don’t renew.
        </Anchor>
      </p>
    );
  }

  return (
    <div className="mb-5 flex flex-col gap-2 border-y border-stone-200 py-5 dark:border-stone-700">
      {planDescription}

      <ExistingPaymentMethodDisplay
        paymentMethod={paymentMethod}
        billingPortalSession={customer.billingPortalSession}
      />

      <p className="text-sm">
        If you have any problems or have questions about your membership,{' '}
        <Anchor
          className="anchor-text whitespace-nowrap"
          href={`mailto:${config.emailAddresses.support}`}
        >
          email us!
        </Anchor>{' '}
        We’re happy to help!
      </p>
    </div>
  );
};

const UnlinkedUser = ({ userId }) => {
  const [stripeUser, setStripeUser] = useState(null);
  const [shouldLocateUser, setShouldLocateUser] = useState(true);
  const [giveupOccurred, setGiveupOccurred] = useState(false);

  const { data } = useSWR(shouldLocateUser ? API_STRIPE_CUSTOMER : null, {
    refreshInterval: 3000,
    shouldRetryOnError: true,
    errorRetryCount: 12,
    errorRetryInterval: 2000,
  });

  // Start a timer for how long we attempt this.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShouldLocateUser(false);
      setGiveupOccurred(true);
    }, MILLISECONDS_TO_WAIT);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  // If we found the user, we can stop waiting.
  useEffect(() => {
    if (data?.customer?.subscription) {
      setStripeUser(data);
      setShouldLocateUser(false);
    }
  }, [data]);

  if (stripeUser) {
    // We found the user!
    return (
      <CurrentSubscriber
        customer={stripeUser.customer}
        paymentMethod={stripeUser.paymentMethod}
        cancelAt={stripeUser.customer.subscription.cancelAt}
        currentPeriodEnd={stripeUser.customer.subscription.currentPeriodEnd}
      />
    );
  } else {
    if (!giveupOccurred) {
      // We're still searching for the user.
      return (
        <div className="card-flat flex flex-col gap-2 border-2 border-amber-500">
          <h3>Standby</h3>
          <p className="text-sm">
            Fetching your Resistbot account now, this should only take a
            few&nbsp;seconds…
          </p>
          {!stripeUser && (
            <BarLoader
              color={'#ffc20e'}
              height={3}
              width={'100%'}
              speedMultiplier={0.2}
            />
          )}
        </div>
      );
    } else {
      // We gave up searching.
      return (
        <div className="card-flat flex flex-col gap-2 border-2 border-red-500">
          <h3>We ran into an error</h3>
          <p className="text-sm">
            We were unable to connect to your Resistbot account. Please{' '}
            <Anchor
              className="anchor-text text-red-500"
              href={`mailto:${config.emailAddresses.support}?subject=Premium%20Upgrade&body=I%27m%20having%20a%20problem%20upgrading%20my%20account%20to%20premium%21%20My%20account%20ID%20is%3A%20${userId}`}
            >
              send us an email
            </Anchor>{' '}
            and our volunteers will fix it up for you right&nbsp;away.
          </p>
        </div>
      );
    }
  }
};

const PersonSummary = ({ customer, paymentMethod, user }) => {
  // Not logged in.
  if (!customer && !user?.id) {
    return <AnonymousUser keyword={'premium'} />;
  }

  // Logged in, No Stripe Customer
  if (!customer && user?.id) {
    return <UnlinkedUser userId={user.id} />;
  }

  // Known Stripe customer, and an active subscription. They shouldn't shift plans here.
  if (hasActiveSubscription(customer)) {
    return (
      <CurrentSubscriber
        customer={customer}
        paymentMethod={paymentMethod}
        cancelAt={customer.subscription.cancelAt}
        currentPeriodEnd={customer.subscription.currentPeriodEnd}
      />
    );
  }
};

export { AnonymousUser, PersonSummary };
